/**
 * Building blocks for an app area's menu, so iManage, Contract Intelligence
 * and Probe share one row size, one label style and one active state.
 *
 * @module components/apps/appAreaNavParts
 */
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import {
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from "../ui/sidebar";

export const APP_AREA_ROW_CLASS = "h-[30px] gap-2.5 px-2.5";

/** One labelled group of rows; the label is optional for the area's top group. */
export function AppAreaNavGroup({
  label,
  children,
}: {
  readonly label?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      {label ? (
        <SidebarGroupLabel className="h-7 text-sidebar-muted-foreground/70">{label}</SidebarGroupLabel>
      ) : null}
      <SidebarMenu className="ps-px">{children}</SidebarMenu>
    </div>
  );
}

/** One row: `render` is the link (a router Link), `icon` a 16px glyph, `count` a quiet badge. */
export function AppAreaNavRow({
  render,
  icon,
  label,
  active = false,
  count,
  muted = false,
  ...rest
}: {
  readonly render: React.ReactElement;
  readonly icon: ReactNode;
  readonly label: string;
  readonly active?: boolean;
  readonly count?: number;
  readonly muted?: boolean;
  readonly [data: `data-${string}`]: string | undefined;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        className={cn(APP_AREA_ROW_CLASS, muted && "text-sidebar-muted-foreground/70")}
        isActive={active}
        render={render}
        {...rest}
      >
        {icon}
        <span className="truncate">{label}</span>
      </SidebarMenuButton>
      {count !== undefined && count > 0 ? (
        <SidebarMenuBadge className="tabular-nums text-sidebar-muted-foreground/70">
          {count}
        </SidebarMenuBadge>
      ) : null}
    </SidebarMenuItem>
  );
}
